import { useState } from "react";
import PropTypes from "prop-types";
import { Button, CircularProgress, TableCell, TableRow } from "@mui/material";
import { useDispatch } from "react-redux";
import { useConfirm } from "material-ui-confirm";
import { applyStoreCommissionRates } from "apis/paymentAPIs";
import { updateSelectedRestaurant } from "store/actions/restaurant";
import { commissionStatus, formatCommissionRates, isCommissionApplied } from "./commissionRates";

const applyDescription = (restaurant, surchargeRuleCount) => {
  const rates = formatCommissionRates(restaurant.adyenPaymentSettings.commissionRates);
  const base =
    `Creates ${restaurant.name || restaurant.id}'s own Adyen split profile (${rates}) ` +
    "and moves the POS store onto it now, instead of waiting for the midnight run.";
  if (surchargeRuleCount === 0) {
    return base;
  }
  if (surchargeRuleCount === null) {
    return `${base} This store's surcharge settings could not be checked; look in View Adyen Config first.`;
  }
  return (
    `${base} This store still has ${surchargeRuleCount} surcharge rules: until its terminals drop them, ` +
    "customers pay the surcharge and the merchant pays commission. Remove them first with " +
    "View Adyen Config > Remove Surcharges."
  );
};

// The Commission Rates rows of the Adyen settings table, with "Apply Now" for rates saved but not yet
// live in Adyen.
const CommissionRatesRows = ({ restaurant, surchargeRuleCount, showSuccessMessage, showErrorMessage }) => {
  const dispatch = useDispatch();
  const confirm = useConfirm();
  const [applying, setApplying] = useState(false);

  const settings = restaurant.adyenPaymentSettings || {};
  const canApply = Boolean(settings.commissionRates) && !isCommissionApplied(settings);

  const applyNow = async () => {
    try {
      setApplying(true);
      const data = await applyStoreCommissionRates(restaurant.id);
      dispatch(updateSelectedRestaurant({
        ...restaurant,
        adyenPaymentSettings: { ...settings, ...data.adyenPaymentSettings },
      }));
      showSuccessMessage("Commission rates applied");
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.error;
      showErrorMessage(detail ? `Failed to apply rates: ${detail}` : "Failed to apply rates");
    } finally {
      setApplying(false);
    }
  };

  const handleApplyNow = () => {
    confirm({
      title: "Apply commission rates now?",
      description: applyDescription(restaurant, surchargeRuleCount),
      confirmationText: "Apply",
    })
      .then(applyNow)
      .catch(() => {
        /* cancelled */
      });
  };

  return (
    <>
      <TableRow>
        <TableCell rowSpan={2}>Commission Rates</TableCell>
        <TableCell>
          <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <span>{commissionStatus(settings)}</span>
            {canApply && (
              <Button color="primary" variant="outlined" size="small" onClick={handleApplyNow} disabled={applying}>
                Apply Now
                {applying && <CircularProgress size={16} style={{ marginLeft: 8 }} />}
              </Button>
            )}
          </div>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell>{formatCommissionRates(settings.commissionRates) || "Not set"}</TableCell>
      </TableRow>
    </>
  );
};

CommissionRatesRows.propTypes = {
  restaurant: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    adyenPaymentSettings: PropTypes.object,
  }).isRequired,
  // null while the store's surcharge settings are loading or unavailable.
  surchargeRuleCount: PropTypes.number,
  showSuccessMessage: PropTypes.func.isRequired,
  showErrorMessage: PropTypes.func.isRequired,
};

export default CommissionRatesRows;
